/* eslint-disable react/no-this-in-sfc */
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSet } from 'react-use';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { EmptyState } from '../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { LineChart } from '../../../CommonComponents/OPL/charts/LineChart/LineChart';
import { formatPercentage } from '../../../CommonUtils/formatNumber';
import { LineChartLoading } from '../../../CommonComponents/OPL/charts/LineChart/LineChart.loading';
import { usePerformanceVsBenchmark } from '../../hooks/usePerformanceVsBenchmark';
import { PeriodSegmentedControl } from '../PeriodSegmentedControl/PeriodSegmentedControl';
import { Dot } from '../../../CommonComponents/OPL/Dot/Dot';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Checkbox } from '../../../CommonComponents/OPL/Checkbox/Checkbox';
import { useIsEmbeddedMobile } from '../../../_hooks/useIsEmbeddedMobile';
import { MobileHeader } from '../MobileHeader/MobileHeader';

const CHART_HEIGHT = 340;

interface Props {
  selectedItemName: string;
  updatedAt: string;
}

export const PerformanceVsBenchmarkChart = ({ selectedItemName, updatedAt }: Props) => {
  const isEmbeddedMobile = useIsEmbeddedMobile();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const { chartTitle } = usePerformanceVsBenchmark();

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {isEmbeddedMobile && (
        <Box mx="16">
          <MobileHeader
            label={selectedItemName}
            subLabel={selectedDate || `Last updated: ${updatedAt}`}
            isHighlighted={!!selectedDate}
          />

          <Text variant="smallCaps" mt="24">
            {chartTitle}
          </Text>
        </Box>
      )}

      <PerformanceVsBenchmarkChartContent setSelectedDate={setSelectedDate} />
    </Box>
  );
};

interface ContentProps {
  setSelectedDate: (date: string | null) => void;
}

const PerformanceVsBenchmarkChartContent = ({ setSelectedDate }: ContentProps) => {
  const { isFetching, isError, refetch, data } = usePerformanceVsBenchmark();

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const [
    ,
    { has: isBenchmarkHidden, toggle: toggleBenchmarkVisibility, reset: resetHiddenBenchmarks },
  ] = useSet();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    resetHiddenBenchmarks();
  }, [isEmbeddedMobile, resetHiddenBenchmarks]);

  const filteredData = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.map(benchmark => ({ ...benchmark, visible: !isBenchmarkHidden(benchmark.name) }));
  }, [data, isBenchmarkHidden]);

  if (isFetching) {
    return (
      <>
        <LineChartLoading
          chartHeight={isEmbeddedMobile ? CHART_HEIGHT : 280}
          showLegend={!isEmbeddedMobile}
          mb="32"
        />

        {isEmbeddedMobile && (
          <Box px="8">
            <PeriodSegmentedControl />
          </Box>
        )}
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          py={isEmbeddedMobile ? '48' : undefined}
          height="100%"
        >
          <ErrorState onAction={refetch} />
        </Box>

        {isEmbeddedMobile && (
          <Box px="8">
            <PeriodSegmentedControl />
          </Box>
        )}
      </>
    );
  }

  if (!data) {
    return null;
  }

  if (data.length === 0) {
    return (
      <>
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          py={isEmbeddedMobile ? '24' : undefined}
        >
          <EmptyState />
        </Box>

        {isEmbeddedMobile && (
          <Box px="8">
            <PeriodSegmentedControl />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      {filteredData && (
        <MemoLineChart
          data={filteredData}
          isEmbeddedMobile={isEmbeddedMobile}
          setSelectedIndex={setSelectedIndex}
          setSelectedDate={setSelectedDate}
        />
      )}

      {isEmbeddedMobile && (
        <Box px="8">
          <PeriodSegmentedControl />
        </Box>
      )}

      {isEmbeddedMobile && (
        <Box px="16" pt="12">
          <HtmlTable variant="embedded" edgePadding={0} style={{ border: 'none' }}>
            <table>
              <tbody>
                <tr style={{ border: 'none' }}>
                  <td>
                    <Text fontSize="h6" fontWeight={600}>
                      Return
                    </Text>
                  </td>

                  <td>
                    <Text fontSize="h6" fontWeight={600} textAlign="right">
                      {formatPercentage(data[0].data[selectedIndex ?? data[0].data.length - 1][1], {
                        mantissa: 2,
                      })}
                    </Text>
                  </td>

                  <td />
                </tr>

                {data.slice(1).map(benchmark => (
                  <tr key={benchmark.name} style={{ border: 'none' }}>
                    <td css="display: flex; align-items: center">
                      <Dot bg={benchmark.color} mr="8" />

                      <Text fontWeight={500}>{benchmark.name}</Text>
                    </td>

                    <td>
                      <Text fontWeight={500} variant="tabular" textAlign="right">
                        {formatPercentage(
                          benchmark.data[selectedIndex ?? benchmark.data.length - 1][1],
                          {
                            mantissa: 2,
                          },
                        )}
                      </Text>
                    </td>

                    <td>
                      <Checkbox
                        checked={!isBenchmarkHidden(benchmark.name)}
                        onChange={() => {
                          toggleBenchmarkVisibility(benchmark.name);
                        }}
                        aria-label="Toggle benchmark visibility"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HtmlTable>
        </Box>
      )}
    </>
  );
};

interface MemoLineChartProps {
  data: { name: string; data: number[][]; color: string }[];
  isEmbeddedMobile: boolean;
  setSelectedIndex: (index: number | null) => void;
  setSelectedDate: (date: string | null) => void;
}

const MemoLineChart = React.memo(
  ({ data, isEmbeddedMobile, setSelectedIndex, setSelectedDate }: MemoLineChartProps) => {
    return (
      <LineChart
        series={data}
        customSettings={{
          legend: {
            enabled: !isEmbeddedMobile,
            y: 6,
            align: 'center',
          },
          xAxis: {
            crosshair: isEmbeddedMobile
              ? {
                  width: 2,
                  dashStyle: 'shortdot',
                }
              : false,
            tickPixelInterval: isEmbeddedMobile ? 85 : 100,
            labels: {
              y: 28,
            },
          },
          yAxis: {
            minTickInterval: 0.0001,
          },
          chart: {
            height: CHART_HEIGHT,
          },
          tooltip: {
            shared: isEmbeddedMobile,
            style: {
              opacity: isEmbeddedMobile ? 0 : 1,
            },
            formatter() {
              if (isEmbeddedMobile) {
                setSelectedIndex(data[0].data.findIndex(([time]) => this.x === time));
                setSelectedDate(moment(this.x).format('MM/DD/YYYY'));
                return null;
              }

              let tooltip = `
                <span style="font-size: 10px">
                  ${moment(this.key).format('ll')}
                </span>
                <br />
              `;

              tooltip += `
                <span style="color:${this.color}">
                  \u25CF
                </span>
        
                ${this.series.name}: 
        
                <span style="font-weight: 600; margin-left: 4px">
                  ${formatPercentage(Number(this.y), { decimalPlaces: 2 })}
                </span>
              `;

              return tooltip;
            },
          },
          plotOptions: {
            series: {
              events: {
                mouseOut() {
                  this.chart.tooltip.hide();
                  setSelectedIndex(null);
                  setSelectedDate(null);
                },
              },
            },
          },
        }}
        xAxisType="datetime"
        yAxisType="percentage"
      />
    );
  },
);
